import React from 'react';
import type { HubTagTemplate, HubOptionTag } from 'dorian-shared/types/branch/HubOption';
import { HUB_MAX_DEFAULT_TAGS } from 'dorian-shared/types/branch/HubOption';
import { HubOptionTagChip } from './HubOptionTagChip';
import './TagDefaultsPicker.scss';

interface TagDefaultsPickerProps {
  tags: HubOptionTag[];
  templates: HubTagTemplate[];
  isDisabled: boolean;
  onChange: (tags: HubOptionTag[]) => void;
}

export function TagDefaultsPicker({
  tags,
  templates,
  isDisabled,
  onChange,
}: TagDefaultsPickerProps) {
  const usedIds = new Set(tags.map((t) => t.tagTemplateId));
  const availableTemplates = templates.filter((t) => t.id !== undefined && !usedIds.has(t.id));
  const atLimit = tags.length >= HUB_MAX_DEFAULT_TAGS;

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = Number(e.target.value);
    if (templateId > 0) {
      const newTag: HubOptionTag = {
        tagTemplateId: templateId,
        sortOrder: tags.length,
      };
      onChange([...tags, newTag]);
      e.target.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const next = tags.filter((_, i) => i !== index)
      .map((t, i) => ({ ...t, sortOrder: i }));
    onChange(next);
  };

  return (
    <div className="tag-defaults-picker d-flex align-items-center flex-wrap">
      {tags.length === 0 && templates.length > 0 && (
        <span className="text-muted small mr-1">no default tags</span>
      )}
      {tags.map((tag, idx) => {
        const template = templates.find((t) => t.id === tag.tagTemplateId);
        return (
          <span key={tag.tagTemplateId} className="tag-defaults-picker__item mr-1">
            <HubOptionTagChip template={template} tagTemplateId={tag.tagTemplateId} />
            {!isDisabled && (
              <button
                type="button"
                className="tag-defaults-picker__remove"
                onClick={() => handleRemove(idx)}
                title="Remove default tag"
              >
                &times;
              </button>
            )}
          </span>
        );
      })}
      {!atLimit && availableTemplates.length > 0 && (
        <select
          className="form-control form-control-sm"
          style={{ width: 150 }}
          value=""
          disabled={isDisabled}
          onChange={handleSelect}
        >
          <option value="">+ Add default..</option>
          {availableTemplates.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.name}{t.text ? ` (${t.text})` : ''}
            </option>
          ))}
        </select>
      )}
      {templates.length === 0 && (
        <span className="text-muted small ml-1">
          No tag templates yet. Create them via the &quot;Tag Templates&quot; button in the episode menu.
        </span>
      )}
    </div>
  );
}
